/**
 * Spawning (GDD §5.3, §6.1): each 800 m section is populated when the previous bridge falls (or a run starts),
 * barges first, then gunboats, helicopters and enemy-jet triggers, ships and barges clear of the island, inside the
 * river minus a margin, 34 m apart. Every random number comes from the section stream.
 *
 * Concept rules: the concept's spawner (L911–L950), draw for draw. GDD rules (decision M4-spawn): the spacing
 * holds within each layer, boats and barges on the water, helicopters in the air, and layouts are drawn so the §6
 * counts always fit.
 */
import { config } from '../core/config.ts';
import { lerp, PERIOD, wrapDz } from '../core/math.ts';
import { acquireEnemy } from '../entities/pool.ts';
import type { GameSim } from '../sim/context.ts';
import { listPush, type Enemy, type EnemyType } from '../sim/state.ts';
import { ISLAND_Z } from '../world/profiles.ts';
import { levelStats, type LevelStats } from './levels.ts';

const HALF_PI = Math.PI / 2;
/** Draw attempts to find a free z before an object is skipped (concept L922). */
const FREE_Z_TRIES = 40;
/** Enemy jets enter this far beyond the bank (concept L949). */
const JET_ENTRY = 70;

function hitBox(type: EnemyType): { halfWidth: number; halfLength: number } {
  const h = config.enemies.hitBox;
  return type === 'ship' ? h.gunboat : type === 'heli' ? h.heli : type === 'fuel' ? h.fuel : h.jet;
}

/**
 * concept spawnEnt (L911–L918): a fresh enemy at (x, z) with its own height, fire cooldown, heading and animation
 * phase. Callers draw their own extras (patrol, sleeper, fire) *before* calling, as the concept evaluates them as
 * arguments, then set them with `finishEnemy`. Returns null if the pool is full (the draws still happen).
 */
export function spawnEnemy(sim: GameSim, type: EnemyType, x: number, z: number): Enemy | null {
  const rand = sim.streams.section;
  const en = config.enemies;
  const y =
    type === 'heli'
      ? en.heliAltitude.min + rand() * (en.heliAltitude.max - en.heliAltitude.min)
      : type === 'jet'
        ? en.jetAltitude
        : 0;
  const cd = 1.2 + rand() * 1.5;
  const face = rand() < 0.5 ? 1 : -1;
  const ph = rand() * 10;
  const e = acquireEnemy(sim.pool, type);
  if (!e) return null;
  const box = hitBox(type);
  e.x = x;
  e.z = z;
  e.y = y;
  e.vx = 0;
  e.vy = 0;
  e.move = false;
  e.near = false;
  e.dying = 0;
  e.spd = 0;
  e.hw = box.halfWidth;
  e.hl = box.halfLength;
  e.cd = cd;
  e.fire = false;
  e.face = face;
  e.yaw = 0;
  e.ph = ph;
  e.px = 0;
  // A fresh model sits at the origin until its first update (concept L909).
  e.posX = e.posY = e.posZ = 0;
  e.rotX = e.rotY = e.rotZ = 0;
  e.prevX = e.prevY = e.prevZ = 0;
  e.posed = false;
  e.wake = false;
  listPush(sim.ents, e);
  return e;
}

/** The heading an enemy starts with, after the caller's overrides (concept L915). */
export function finishEnemy(e: Enemy): void {
  e.yaw = e.type === 'fuel' ? 0 : e.face > 0 ? -HALF_PI : HALF_PI;
  e.wake = false;
}

/** concept freeZ (L921–L929): a z in [zA, zB] at least 34 m from the others, optionally 75 m clear of the island. */
function freeZ(sim: GameSim, zA: number, zB: number, avoidIsland: boolean, used: { n: number }): number | null {
  const rand = sim.streams.section;
  const sp = config.spawning;
  for (let k = 0; k < FREE_Z_TRIES; k++) {
    const zz = lerp(zA, zB, rand());
    let ok = true;
    if (avoidIsland && Math.abs(wrapDz(zz - ISLAND_Z)) < sp.islandClear) continue;
    for (let j = 0; j < used.n; j++)
      if (Math.abs((sim.slots[j] ?? 0) - zz) < sp.minSpacing) {
        ok = false;
        break;
      }
    if (ok) {
      sim.slots[used.n++] = zz;
      return zz;
    }
  }
  return null;
}

/** concept laneX (L930): sideways placement inside the river minus a margin. */
function laneX(sim: GameSim, zz: number, margin: number): number {
  const c = sim.W.center(zz);
  const w = sim.W.width(zz);
  return c + (sim.streams.section() * 2 - 1) * Math.max(w - margin, 2);
}

// Scratch objects reused by every call (no allocation while spawning).
const used = { n: 0 };
const stats: LevelStats = levelStats(1);

/** Populates z from zA to zB (zA nearer the jet) for the current level, by the run's rules. */
export function spawnSection(sim: GameSim, zA: number, zB: number): void {
  if (sim.rules === 'gdd') spawnLayered(sim, zA, zB);
  else spawnConcept(sim, zA, zB);
}

/** concept spawnSection (L919–L946). */
function spawnConcept(sim: GameSim, zA: number, zB: number): void {
  const rand = sim.streams.section;
  const L = sim.run.level;
  const st = levelStats(L, config, stats);
  const d = config.difficulty;
  const sp = config.spawning;
  const pat = config.enemies.patrolSpeed;
  const sleeper = config.enemies.sleeperShare;
  used.n = 0;
  for (let i = 0; i < st.fuel; i++) {
    const z = freeZ(sim, zA, zB, true, used);
    if (z === null) continue;
    const x = laneX(sim, z, sp.margin.fuel);
    const e = spawnEnemy(sim, 'fuel', x, z);
    if (e) finishEnemy(e);
  }
  for (let i = 0; i < st.gunboats; i++) {
    const z = freeZ(sim, zA, zB, true, used);
    if (z === null) continue;
    const unlocked = L >= d.gunboatPatrol.fromLevel;
    const mv = unlocked && rand() < st.gunboatPatrol;
    const x = laneX(sim, z, sp.margin.gunboat);
    const near = unlocked && !mv && rand() < sleeper;
    const fire = L >= d.gunboatFlak.fromLevel && rand() < st.gunboatFlak;
    const e = spawnEnemy(sim, 'ship', x, z);
    if (!e) continue;
    e.move = mv;
    e.near = near;
    e.spd = pat.gunboat.base + L * pat.gunboat.perLevel;
    e.fire = fire;
    finishEnemy(e);
  }
  for (let i = 0; i < st.helis; i++) {
    const z = freeZ(sim, zA, zB, false, used);
    if (z === null) continue;
    const unlocked = L >= d.heliPatrol.fromLevel;
    const mh = unlocked && rand() < st.heliPatrol;
    const x = laneX(sim, z, sp.margin.heli);
    const near = unlocked && !mh && rand() < sleeper;
    const fire = L >= d.heliFire.fromLevel && rand() < st.heliFire;
    const e = spawnEnemy(sim, 'heli', x, z);
    if (!e) continue;
    e.move = mh;
    e.near = near;
    e.spd = pat.heli.base + L * pat.heli.perLevel;
    e.fire = fire;
    finishEnemy(e);
  }
  for (let i = 0; i < st.jets; i++) {
    const zt = lerp(zA - 60, zB + 60, rand());
    if (sim.jetTrigCount < sim.jetTrig.length) sim.jetTrig[sim.jetTrigCount++] = zt;
  }
  // The autopilot's bridge range for this section (concept L945).
  sim.run.bridgeRange = rand() < 0.25 ? 140 + rand() * 40 : 235 + rand() * 60;
}

/** concept spawnEnemyJet (L947–L950): an enemy jet enters 85–140 m ahead from either bank and crosses. */
export function spawnEnemyJet(sim: GameSim): void {
  const rand = sim.streams.section;
  const en = config.enemies;
  const side = rand() < 0.5 ? 1 : -1;
  const z = sim.jet.z - (en.jetAhead.min + rand() * (en.jetAhead.max - en.jetAhead.min));
  const c = sim.W.center(z);
  const w = sim.W.width(z);
  const e = spawnEnemy(sim, 'jet', c - side * (w + JET_ENTRY), z);
  if (!e) return;
  e.face = side;
  e.vx = side * (en.patrolSpeed.jet.base + sim.run.level * en.patrolSpeed.jet.perLevel);
  finishEnemy(e);
}

// ---------- GDD rules: layered, always-fitting layouts (decision M4-spawn) ----------

/** Free stretches of the section for one layer, as [lo, hi] pairs (z ascending). */
const spans = new Float64Array(8);
/** Water layer: z, x and hit half-width of each boat or barge placed so far (for the helicopters' clearance). */
const waterZ = new Float64Array(32);
const waterX = new Float64Array(32);
const waterHw = new Float64Array(32);
const isFuel = new Uint8Array(32);
const order = new Int32Array(32);
const airZ = new Float64Array(32);

/** The section [zB, zA] minus 75 m either side of each island; returns the number of stretches. */
function waterSpans(zA: number, zB: number): number {
  const clear = config.spawning.islandClear;
  let n = 0;
  let lo = zB;
  const mid = (zA + zB) / 2;
  const first = mid + wrapDz(ISLAND_Z - mid) - PERIOD;
  for (let k = 0; k < 3; k++) {
    const ic = first + k * PERIOD;
    const a = ic - clear;
    const b = ic + clear;
    if (b <= lo || a >= zA) continue;
    if (a > lo) {
      spans[n++] = lo;
      spans[n++] = a;
    }
    lo = Math.max(lo, b);
  }
  if (zA > lo) {
    spans[n++] = lo;
    spans[n++] = zA;
  }
  return n / 2;
}

/**
 * Up to `n` positions in the stretches, at least `s` apart, drawn uniformly over all such layouts: sorted uniform
 * draws in [0, T − (n − 1)·s] plus i·s along the stretches laid end to end (the island's gap only adds distance).
 * Fewer when they can't fit. Returns how many were placed, z ascending, in `out`.
 */
function layout(sim: GameSim, count: number, n: number, s: number, out: Float64Array): number {
  const rand = sim.streams.section;
  let total = 0;
  for (let i = 0; i < count; i++) total += (spans[2 * i + 1] ?? 0) - (spans[2 * i] ?? 0);
  let m = n;
  while (m > 0 && (m - 1) * s > total) m--;
  const free = total - (m - 1) * s;
  for (let i = 0; i < m; i++) out[i] = rand() * free;
  // Insertion sort: a handful of values, no allocation.
  for (let i = 1; i < m; i++) {
    const v = out[i] ?? 0;
    let j = i - 1;
    for (; j >= 0 && (out[j] ?? 0) > v; j--) out[j + 1] = out[j] ?? 0;
    out[j + 1] = v;
  }
  for (let i = 0; i < m; i++) {
    let v = (out[i] ?? 0) + i * s;
    let z = spans[2 * count - 1] ?? 0;
    for (let k = 0; k < count; k++) {
      const lo = spans[2 * k] ?? 0;
      const len = (spans[2 * k + 1] ?? 0) - lo;
      if (v <= len) {
        z = lo + v;
        break;
      }
      v -= len;
    }
    out[i] = z;
  }
  return m;
}

/** Blocked stretches of a helicopter's lane (sorted, merged), for the boats beside it. */
const blockLo = new Float64Array(32);
const blockHi = new Float64Array(32);

/**
 * A helicopter's lane (inside the river minus its margin), drawn uniformly over the part not beside a boat within
 * the layer spacing along the river, so no helicopter starts stacked on a boat. One draw; where boats fill the
 * whole lane (a narrow reach), the lane's edge farthest from them.
 */
function heliLane(sim: GameSim, z: number, nWater: number): number {
  const sp = config.spawning;
  const heliHw = config.enemies.hitBox.heli.halfWidth;
  const c = sim.W.center(z);
  const lim = Math.max(sim.W.width(z) - sp.margin.heli, 2);
  const lo = c - lim;
  const hi = c + lim;
  let n = 0;
  for (let i = 0; i < nWater; i++) {
    if (Math.abs((waterZ[i] ?? 0) - z) >= sp.minSpacing) continue;
    const r = heliHw + (waterHw[i] ?? 0);
    const a = Math.max(lo, (waterX[i] ?? 0) - r);
    const b = Math.min(hi, (waterX[i] ?? 0) + r);
    if (b <= a) continue;
    // Insert sorted by start.
    let j = n++;
    for (; j > 0 && (blockLo[j - 1] ?? 0) > a; j--) {
      blockLo[j] = blockLo[j - 1] ?? 0;
      blockHi[j] = blockHi[j - 1] ?? 0;
    }
    blockLo[j] = a;
    blockHi[j] = b;
  }
  // Free length of the lane outside the blocked stretches.
  let free = 0;
  let cur = lo;
  for (let k = 0; k < n; k++) {
    const a = blockLo[k] ?? 0;
    const b = blockHi[k] ?? 0;
    if (a > cur) free += a - cur;
    cur = Math.max(cur, b);
  }
  if (hi > cur) free += hi - cur;
  const u = sim.streams.section();
  if (free <= 1e-6) {
    // A narrow reach with boats across the whole lane: the lane edge farthest from them.
    let dLo = Infinity;
    let dHi = Infinity;
    for (let k = 0; k < n; k++) {
      const mid = ((blockLo[k] ?? 0) + (blockHi[k] ?? 0)) / 2;
      dLo = Math.min(dLo, Math.abs(mid - lo));
      dHi = Math.min(dHi, Math.abs(mid - hi));
    }
    return dLo >= dHi ? lo : hi;
  }
  let v = u * free;
  cur = lo;
  for (let k = 0; k < n; k++) {
    const a = blockLo[k] ?? 0;
    const b = blockHi[k] ?? 0;
    if (a > cur) {
      if (v <= a - cur) return cur + v;
      v -= a - cur;
    }
    cur = Math.max(cur, b);
  }
  return Math.min(hi, cur + v);
}

function spawnLayered(sim: GameSim, zA: number, zB: number): void {
  const rand = sim.streams.section;
  const L = sim.run.level;
  const st = levelStats(L, config, stats);
  const d = config.difficulty;
  const sp = config.spawning;
  const hb = config.enemies.hitBox;
  const pat = config.enemies.patrolSpeed;
  const sleeper = config.enemies.sleeperShare;

  // Water: one layout for barges and gunboats; barges keep their places first if a short section can't hold all.
  const nw = layout(sim, waterSpans(zA, zB), st.fuel + st.gunboats, sp.minSpacing, waterZ);
  const nFuel = Math.min(st.fuel, nw);
  for (let i = 0; i < nw; i++) {
    order[i] = i;
    isFuel[i] = 0;
  }
  for (let k = 0; k < nFuel; k++) {
    const j = k + Math.floor(rand() * (nw - k));
    const t = order[k] ?? 0;
    order[k] = order[j] ?? 0;
    order[j] = t;
    isFuel[order[k] ?? 0] = 1;
  }
  for (let pass = 0; pass < 2; pass++)
    for (let i = 0; i < nw; i++) {
      const fuel = isFuel[i] === 1;
      if (fuel !== (pass === 0)) continue;
      const z = waterZ[i] ?? 0;
      if (fuel) {
        const x = laneX(sim, z, sp.margin.fuel);
        waterX[i] = x;
        waterHw[i] = hb.fuel.halfWidth;
        const e = spawnEnemy(sim, 'fuel', x, z);
        if (e) finishEnemy(e);
        continue;
      }
      const unlocked = L >= d.gunboatPatrol.fromLevel;
      const mv = unlocked && rand() < st.gunboatPatrol;
      const x = laneX(sim, z, sp.margin.gunboat);
      waterX[i] = x;
      waterHw[i] = hb.gunboat.halfWidth;
      const near = unlocked && !mv && rand() < sleeper;
      const fire = L >= d.gunboatFlak.fromLevel && rand() < st.gunboatFlak;
      const e = spawnEnemy(sim, 'ship', x, z);
      if (!e) continue;
      e.move = mv;
      e.near = near;
      e.spd = pat.gunboat.base + L * pat.gunboat.perLevel;
      e.fire = fire;
      finishEnemy(e);
    }

  // Air: helicopters spaced among themselves over the whole section, clear of the boats beside them.
  spans[0] = zB;
  spans[1] = zA;
  const na = layout(sim, 1, st.helis, sp.minSpacing, airZ);
  for (let i = 0; i < na; i++) {
    const z = airZ[i] ?? 0;
    const unlocked = L >= d.heliPatrol.fromLevel;
    const mh = unlocked && rand() < st.heliPatrol;
    const x = heliLane(sim, z, nw);
    const near = unlocked && !mh && rand() < sleeper;
    const fire = L >= d.heliFire.fromLevel && rand() < st.heliFire;
    const e = spawnEnemy(sim, 'heli', x, z);
    if (!e) continue;
    e.move = mh;
    e.near = near;
    e.spd = pat.heli.base + L * pat.heli.perLevel;
    e.fire = fire;
    finishEnemy(e);
  }

  for (let i = 0; i < st.jets; i++) {
    const zt = lerp(zA - 60, zB + 60, rand());
    if (sim.jetTrigCount < sim.jetTrig.length) sim.jetTrig[sim.jetTrigCount++] = zt;
  }
  sim.run.bridgeRange = rand() < 0.25 ? 140 + rand() * 40 : 235 + rand() * 60;
}
